import { Dayjs } from 'dayjs'

import { getOnlyDate } from '~/astra'
import { StoreState } from '~/store/store'
import { needleToTokens } from '~/utils/highlightNeedlesDebounced'

import { Include, Location, Practitioner, PractitionerScheduleLocation, selectPractitionerScheduleLocation, usePractitionerScheduleLocations } from '../'
import { BOOKING_PRACTITIONER_INCLUDE } from './practitioners'

export const SCHEDULE_PRACTITIONER_INCLUDE = { departments: true } as const

const SCHEDULED_PRACTITIONER_INCLUDE = { practitioner: { dipsResource: true } } as const
const SCHEDULED_PRACTITIONER_WITH_DEPARTMENTS_INCLUDE = { practitioner: SCHEDULE_PRACTITIONER_INCLUDE } as const

export const BOOKING_STAFFING_PLAN_PRACTITIONER_INCLUDE = { practitioner: BOOKING_PRACTITIONER_INCLUDE } as const

export const PRACTITIONER_SCHEDULE_LOCATION_CELL_INCLUDE = { location: true } as const
export type PractitionerScheduleLocationCell = Include<PractitionerScheduleLocation, typeof PRACTITIONER_SCHEDULE_LOCATION_CELL_INCLUDE>

export const PRACTITIONER_CELL_LOCATION_INCLUDE = { departments: true } as const
export type PractitionerCellLocation = Include<Location, typeof PRACTITIONER_CELL_LOCATION_INCLUDE>

// Optimistic first, then newest updated_at — mirrors legacy selectOrderedPractitionerScheduleLocations + reverse.
const PRACTITIONER_SCHEDULE_LOCATION_CELL_ORDER_BY = [
    (a: PractitionerScheduleLocation, b: PractitionerScheduleLocation) => (a.isOptimistic ? 0 : 1) - (b.isOptimistic ? 0 : 1),
    (a: PractitionerScheduleLocation, b: PractitionerScheduleLocation) => b.updated_at.localeCompare(a.updated_at),
] as const

// Oldest updated_at first, non-optimistic before optimistic — mirrors legacy byDateLocationId ordering.
export const LOCATION_DAY_SCHEDULE_ORDER_BY = [
    (a: PractitionerScheduleLocation, b: PractitionerScheduleLocation) => a.updated_at.localeCompare(b.updated_at),
    (a: PractitionerScheduleLocation, b: PractitionerScheduleLocation) => (a.isOptimistic ? 1 : 0) - (b.isOptimistic ? 1 : 0),
] as const

export type ScheduledPractitioner = Include<Practitioner, { dipsResource: true }>
export type ScheduledPractitionerWithDepartments = Include<Practitioner, typeof SCHEDULE_PRACTITIONER_INCLUDE>
export type SchedulePractitioner = ScheduledPractitionerWithDepartments

function useLocationDayScheduleLocations<Inc extends typeof SCHEDULED_PRACTITIONER_INCLUDE | typeof SCHEDULED_PRACTITIONER_WITH_DEPARTMENTS_INCLUDE>(
    date: Dayjs,
    locationId: number,
    include: Inc
) {
    return usePractitionerScheduleLocations({
        where: {
            scheduled_date: getOnlyDate(date),
            location_id: locationId,
        },
        include,
        orderBy: LOCATION_DAY_SCHEDULE_ORDER_BY,
    })
}

export function scheduledPractitionersMatchNeedle(practitioners: readonly Pick<Practitioner, 'short_name'>[], text: string): boolean {
    const tokens = needleToTokens(text)
    if (tokens.length === 0) return true

    const scheduledPractitionerNames = practitioners.map(practitioner => practitioner.short_name.toLocaleLowerCase())
    return tokens.some(token => scheduledPractitionerNames.some(name => name.includes(token)))
}

export function useScheduledPractitionersForLocationDay(date: Dayjs, locationId: number): ScheduledPractitioner[] {
    return useLocationDayScheduleLocations(date, locationId, SCHEDULED_PRACTITIONER_INCLUDE)
        .map(schedule => schedule.practitioner)
        .filter(Boolean)
}

export function useScheduledPractitionersWithDepartmentsForLocationDay(date: Dayjs, locationId: number): ScheduledPractitionerWithDepartments[] {
    return useLocationDayScheduleLocations(date, locationId, SCHEDULED_PRACTITIONER_WITH_DEPARTMENTS_INCLUDE)
        .map(schedule => schedule.practitioner)
        .filter(Boolean)
}

export function usePractitionerScheduleLocationsForCell(practitionerId: number, scheduledDate: string): PractitionerScheduleLocationCell[] {
    return usePractitionerScheduleLocations({
        where: { practitioner_id: practitionerId, scheduled_date: scheduledDate },
        include: PRACTITIONER_SCHEDULE_LOCATION_CELL_INCLUDE,
        orderBy: PRACTITIONER_SCHEDULE_LOCATION_CELL_ORDER_BY,
    })
}

export function selectPractitionerScheduleLocationForCell(state: StoreState, date: Dayjs, locationId: number, practitionerId: number) {
    return selectPractitionerScheduleLocation(state, {
        where: {
            scheduled_date: getOnlyDate(date),
            location_id: locationId,
            practitioner_id: practitionerId,
        },
    })
}
